
import { sha256 } from 'js-sha256';
import moment from 'moment';
import aliases from 'react-emoji-render/data/aliases';
import emojiAsciiAliases from 'react-emoji-render/data/asciiAliases';

import { apiKEY } from '../base/config/constants';
import { fetchAxiosApi } from '../base/mtApi';
import { escapeRegexp } from '../base/util';
import { checkIfReactNative, isDomainWeborNative } from '../base/util/checkOS';

/**
 * An ASCII emoticon regexp array to find and replace old-style ASCII
 * emoticons (such as :O) to new Unicode representation, so then devices
 * and browsers that support them can render these natively without
 * a 3rd party component.
 *
 * NOTE: this is currently only used on mobile, but it can be used
 * on web too once we drop support for browsers that don't support
 * unicode emoji rendering.
 */
const EMOTICON_REGEXP_ARRAY: Array<Array<Object>> = [];

(function() {
    for (const [ key, value ] of Object.entries(aliases)) {
        let escapedValues;
        const asciiEmojies = emojiAsciiAliases[key];

        // Adding ascii emoticons
        if (asciiEmojies) {
            escapedValues = asciiEmojies.map(v => escapeRegexp(v));
        } else {
            escapedValues = [];
        }

        // Adding slack-type emoji format
        escapedValues.push(escapeRegexp(`:${key}:`));

        const regexp = `\\B(${escapedValues.join('|')})\\B`;

        EMOTICON_REGEXP_ARRAY.push([ new RegExp(regexp, 'g'), value ]);
    }
})();

/**
 * Replaces ascii and other non-unicode emoticons with unicode emojis to let the emojis be rendered
 * by the platform native renderer.
 *
 * @param {string} message - The message to parse and replace.
 * @returns {string}
 */
export function replaceNonUnicodeEmojis(message: string) {
    let replacedMessage = message;

    for (const [ regexp, replaceValue ] of EMOTICON_REGEXP_ARRAY) {
        replacedMessage = replacedMessage.replace(regexp, replaceValue);
    }

    return replacedMessage;
}

/**
 * Selector for calculating the number of unread chat messages.
 *
 * @param {Object} state - The redux state.
 * @returns {number} The number of unread messages.
 */
export function getUnreadCount(state: Object) {
    const { lastReadMessage, messages } = state['features/chat'];

    const messagesCount = messages.length;

    if (!messagesCount) {
        return 0;
    }

    let reactionMessages = 0;

    if (navigator.product === 'ReactNative') {
    // React native stores the messages in a reversed order.
        const lastReadIndex = messages.indexOf(lastReadMessage);

        if (lastReadIndex === -1) {
            for (let i = 0; i < messagesCount; i++) {
                if (messages[i].isReaction) {
                    reactionMessages++;
                }
            }

            return messagesCount - reactionMessages;
        }

        for (let i = 0; i < lastReadIndex; i++) {
            if (messages[i].isReaction) {
                reactionMessages++;
            }
        }

        return lastReadIndex - reactionMessages;
    }

    const lastReadIndex = messages.lastIndexOf(lastReadMessage);

    for (let i = lastReadIndex + 1; i < messagesCount; i++) {
        if (messages[i].isReaction) {
            reactionMessages++;
        }
    }


    return messagesCount - (lastReadIndex + 1) - reactionMessages;
}

/**
 * Selector for calculating the number of unread chat messages.
 *
 * @param {Object} state - The redux state.
 * @returns {number} The number of unread messages.
 */
export function getUnreadMessagesCount(state: Object) {
    const nbUnreadMessages = state['features/chat']?.nbUnreadMessages;

    return nbUnreadMessages;
}

/**
 * Get whether the chat smileys are disabled or not.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} The disabled flag.
 */
export function areSmileysDisabled(state: Object) {
    const disableChatSmileys
    = state['features/base/config']?.disableChatSmileys === true;

    return disableChatSmileys;
}

/**
 * 描述.
 *
 * @param {any} url - URL.
 * @returns {any}
 */
export function getFilename(url) {
    if (url) {
        return url.substr(url.lastIndexOf('/') + 1);
    }

    return '';
}

/**
 * Generates the message text to be rendered in the component.
 *
 * @param {Object} message - The message from which to get the text.
 * @returns {string}
 */
export function getMessageText(message: Object) {
    return message.message;
}


/**
 * Constructs the FormData required for uploading chat files.
 *
 * @param {Object} payloadData - The data required for the file upload.
 * @returns {FormData} The constructed FormData object.
 */
export function getUploadFormData(payloadData) {
    const formData = new FormData();
    const moement = moment().seconds(0)
                    .milliseconds(0)
                    .format('X');

    const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
    const parameters = isDomainWeborNative;

    formData.append('credentials', mesh256);
    formData.append('client_id', payloadData.mhClientID);
    formData.append('meeting_id', payloadData.meetingID);

    if (checkIfReactNative()) {
        formData.append('package_id', parameters.package_id);
    } else {
        formData.append('domain', parameters.domain);
    }
    if (payloadData.email) {
        formData.append('user_email', payloadData.email);
    }

    payloadData.files && payloadData.files.forEach(file => {
        formData.append('uploaded_file', file);
    });

    return formData;
}

/**
 * Uploads a chat file to the server.
 *
 * @param {Object} payloadData - The data required for the file upload.
 * @param {string} payloadData.mhClientID - The client ID.
 * @param {string} payloadData.meetingID - The meeting ID.
 * @param {string} payloadData.accessToken - The access token for authorization.
 * @param {Array<File>} [payloadData.files] - The files to be uploaded.
 * @param {string} [payloadData.email] - The user's email address.
 * @param {Object} [payloadData.config] - Additional configuration options.
 * @returns {Promise<Object>} The response data from the server.
 */
export async function postChatUploadedFile(payloadData) {
    const formData = getUploadFormData(payloadData);

    try {
        const headers = {
            Authorization: `Bearer ${payloadData.accessToken}`,
            'Content-Type': 'multipart/form-data'
        };
        const responseData = await fetchAxiosApi('customer/savemeetinguploadedfile', {
            method: 'post',
            headers,
            body: formData,
            config: payloadData.config
        });

        return responseData;
    } catch (error) {
        throw new Error(`Upload failed: ${JSON.stringify(error)}`);
    }
}

/**
 * Constructs the JSON payload required for deleting chat files.
 *
 * @param {Object} payloadData - The data required for the file delete.
 * @returns {Object} The constructed JSON object.
 */
export function getDeleteJsonData(payloadData) {
    const moement = moment().seconds(0)
                    .milliseconds(0)
                    .format('X');

    const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
    const parameters = isDomainWeborNative;

    const body = {
        credentials: mesh256,
        client_id: payloadData.mhClientID,
        meeting_id: payloadData.meetingID
    };

    if (checkIfReactNative()) {
        body.package_id = parameters.package_id;
    } else {
        body.domain = parameters.domain;
    }

    if (payloadData.uploadedS3Url) {
        body.uploaded_s3_url = payloadData.uploadedS3Url;
    }

    return body;
}

/**
 * Deletes a chat file from the server.
 *
 * @param {Object} payloadData - The data required for the file delete.
 * @param {string} payloadData.mhClientID - The client ID.
 * @param {string} payloadData.meetingID - The meeting ID.
 * @param {string} payloadData.accessToken - The access token for authorization.
 * @param {string} payloadData.uploadedS3Url - The URL of the file to be deleted.
 * @param {Object} [payloadData.config] - Additional configuration options.
 * @returns {Promise<Object>} The response data from the server.
 */
export async function postDeleteChatUploadedFile(payloadData) {
    const bodyData = getDeleteJsonData(payloadData);

    try {
        const headers = {
            Authorization: `Bearer ${payloadData.accessToken}`,
            'Content-Type': 'application/json'
        };
        const responseData = await fetchAxiosApi('customer/deletemeetinguploadedfile', {
            method: 'post',
            headers,
            body: bodyData,
            config: payloadData.config
        });

        return responseData;
    } catch (error) {
        throw new Error(`Delete failed: ${JSON.stringify(error)}`);
    }
}

